import { Bell } from "@phosphor-icons/react"
import {
  Checkbox,
  FormInput,
  FormSelector,
  Modal,
  RadioGroup,
  TextArea,
  Toast,
} from "@vesatogo/grass-core"
import { FormDateInput } from "@vesatogo/grass-dates"
import classNames from "classnames"
import dayjs from "dayjs"
import { useState } from "react"
import toast from "react-hot-toast"
import { OperationContext } from "urql"
import { Dropzone } from "~/components/Dropzone"
import { ALERTS } from "~/constants/Alerts"
import { useAddUpdateStorageLocationsMutation } from "~/generated/graphql"

export type RenewAgreementProps = {
  isOpen: boolean
  onClose: () => void
  getInputProps: (key: string) => any
  getFileUploadProps: (key: string, maxFiles?: number) => any
  rentTypeOptions?: any[]
  reset?: () => void
  refetch: (opts?: Partial<OperationContext> | undefined) => void
  parentData: {
    name: string
    capacity: number
    capacity_unit_id: string
    agreementDate?: string
  }
  state?: any
  storageLocationId?: string
  setFieldValue?: any
}

export const RenewAgreement: React.FC<RenewAgreementProps> = ({
  isOpen,
  onClose,
  getInputProps,
  getFileUploadProps,
  rentTypeOptions = [],
  reset,
  refetch,
  parentData,
  state,
  storageLocationId,
  setFieldValue,
}) => {
  const [{ fetching: submitting }, addSubLocation] =
    useAddUpdateStorageLocationsMutation()
  const [isSubmitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    setSubmitting(true)

    if (
      !state?.attachments?.length ||
      !state?.agreement_no ||
      !state?.name ||
      !state?.start_date ||
      !state?.end_date
    ) {
      toast.custom(t => (
        <Toast
          title="Error"
          subtitle="Please fill all the required fields"
          intent="danger"
          id={t.id}
          onClose={() => toast.dismiss(t.id)}
        />
      ))
      return
    }

    const { error } = await addSubLocation({
      input: {
        id: storageLocationId,
        name: parentData.name,
        capacity: parentData.capacity,
        capacity_unit_id: parentData.capacity_unit_id,
        agreement: {
          name: state?.name,
          ownership_kind: state?.ownership_kind,
          start_date: state.start_date
            ? dayjs(state.start_date).format("YYYY-MM-DD")
            : null,
          end_date: state.end_date
            ? dayjs(state.end_date).format("YYYY-MM-DD")
            : null,
          lease_amount: state.lease_amount ? Number(state.lease_amount) : null,
          lease_tenure_kind: state.lease_tenure_kind?.name,
          agreement_no: state.agreement_no,
          tnc: state.tnc,
          attachments_ids: state.attachments?.map((item: any) => item.id),
          expiry_alert_days: state.alert?.days,
        },
      },
    })

    if (error) {
      toast.error(error.message)
    } else {
      toast.success("Sub-location added successfully")
      onClose()
      reset?.()
      refetch({
        requestPolicy: "network-only",
      })
    }
    setSubmitting(false)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        reset?.()
        onClose()
        setSubmitting(false)
      }}
      title="Renew Agreement"
      bodyClassName="max-w-2xl"
      contentClassName="overflow-y-auto max-h-[calc(100vh-300px)]"
      footerClassName="sticky bottom-0"
      primaryActionButtonProps={{
        text: "Send for Approval",
        onClick: handleSubmit,
        loading: submitting,
      }}
      secondaryActionButtonProps={{
        text: "Cancel",
        onClick: () => {
          reset?.()
          onClose()
          setSubmitting(false)
        },
        variant: "outline",
        disabled: submitting,
      }}
    >
      <div className="p-2 flex flex-col gap-4">
        <p className="text-sm text-gray-500">
          Update the details for the new lease term for the current tenant.
        </p>

        <div className="grid grid-cols-2 gap-4">
          <FormInput
            {...getInputProps("name")}
            label="Agreement Person Name"
            required
            placeholder="Enter"
            className={"col-span-2"}
            error={
              isSubmitting && !state?.name
                ? "Please enter agreement person name"
                : ""
            }
          />
          <FormDateInput
            {...getInputProps("start_date")}
            label="Start Date"
            required
            placeholder="Select"
            excludeDate={date =>
              !dayjs(date).isAfter(parentData.agreementDate, "day")
            }
            error={
              isSubmitting && !state?.start_date
                ? "Please enter start date"
                : ""
            }
          />
          <FormDateInput
            {...getInputProps("end_date")}
            label="End Date"
            required
            placeholder="Select"
            excludeDate={date => !dayjs(date).isAfter(state?.start_date, "day")}
            error={
              isSubmitting && !state?.end_date ? "Please enter end date" : ""
            }
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <RadioGroup
            className={"mb-3 flex"}
            label="Type of Store"
            required
            {...getInputProps("ownership_kind")}
          >
            <Checkbox type={"radio"} value={"Own"} label={"Own"} />
            <Checkbox type={"radio"} value={"Lease"} label={"Rent"} />
          </RadioGroup>
          <FormSelector
            label="Rent Type"
            placeholder="Select"
            options={[
              { id: "Monthly", name: "Monthly" },
              { id: "Yearly", name: "Yearly" },
            ]}
            {...getInputProps("lease_tenure_kind")}
          />

          <FormInput
            label="Rent Amount"
            placeholder="Enter"
            {...getInputProps("lease_amount")}
          />
        </div>

        <FormInput
          {...getInputProps("agreement_no")}
          label="Agreement No."
          required
          placeholder="Enter"
          error={
            isSubmitting && !state?.agreement_no
              ? "Please enter agreement number"
              : ""
          }
        />

        <TextArea
          {...getInputProps("description")}
          label="Description"
          required
          placeholder="Enter"
          className="w-full"
          error={
            isSubmitting && !state?.description
              ? "Please enter description"
              : ""
          }
        />

        <TextArea
          {...getInputProps("tnc")}
          label="Terms & Conditions"
          placeholder="Enter"
          className="w-full"
        />

        <div className="flex flex-col gap-1">
          <label className="text-sm font-500 text-gray-700">
            Upload Agreement <span className="text-red-500">*</span>
          </label>

          <Dropzone
            {...getFileUploadProps("attachments", 5)}
            onError={toast.error}
            endpoint={`https://${
              import.meta.env.VITE_REST_URL
            }/rest/trade/uploader/`}
            appName="setutrade"
            environment={import.meta.env.VITE_APP_ENV}
            method="put"
            multiple
          />
        </div>

        <div className="text-sm">
          <div className="flex items-center gap-2 mb-4">
            <Bell size={20} />
            <h3 className="font-500 text-gray-800">Alert Settings</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Select when you want to receive notifications before the agreement
            expires.
          </p>

          <div className="flex flex-col gap-3 w-full">
            {ALERTS.map(alert => {
              const isSelected = alert.id === state?.alert?.id
              return (
                <div
                  key={alert.id}
                  onClick={() => {
                    setFieldValue("alert", alert)
                  }}
                  className={classNames(
                    "p-3 border rounded border-gray-300 rounded-lg flex flex-col gap-1 cursor-pointer",
                    isSelected ? "border-green-150 bg-green-100" : ""
                  )}
                >
                  <div className="flex items-baseline gap-2">
                    <Checkbox checked={isSelected} />
                    <div className="text-sm flex flex-col">
                      <span className="font-600">{alert.label}</span>
                      <span className="text-xs text-gray-600">
                        {alert.description}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </Modal>
  )
}
